import { useQuery } from "@tanstack/react-query";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { ProviderFixturePlayers } from "@/lib/sports/providers/types";
import type { MatchLifecycle } from "@/lib/dataPolicy";

export interface UseMatchPlayerStatsResult {
  data: ProviderFixturePlayers | undefined;
  isLoading: boolean;
  isError: boolean;
  updatedAt: number | undefined;
}

/**
 * Real Data Phase 4 — Official per-fixture player statistics
 * (API-Football `/fixtures/players`).
 *
 * Never fabricates. Refetch cadence follows match lifecycle:
 *  - SCHEDULED: disabled (pre-match, no player stats exist yet).
 *  - LIVE:      every 60s.
 *  - FINISHED:  one-shot, no polling.
 */
export function useMatchPlayerStats(
  fixtureId: string | undefined,
  lifecycle: MatchLifecycle,
): UseMatchPlayerStatsResult {
  const enabled =
    typeof fixtureId === "string" && fixtureId.length > 0 && lifecycle !== "SCHEDULED";
  const refetchInterval = lifecycle === "LIVE" ? 60_000 : false;
  const queryKey = ["match-player-stats", fixtureId ?? "__none__"] as const;
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchPlayerStats][mount]", { enabled, fixtureId, lifecycle, refetchInterval, queryKey });
  }
  const q = useQuery<ProviderFixturePlayers>({
    queryKey,
    queryFn: async () => {
      const t0 = Date.now();
      console.info("[TRACE][useMatchPlayerStats][queryFn:start]", { fixtureId, lifecycle, at: new Date().toISOString() });
      try {
        const r = await unifiedSports.getFixturePlayers(fixtureId as string);
        console.info("[TRACE][useMatchPlayerStats][queryFn:success]", { fixtureId, ms: Date.now() - t0 });
        return r;
      } catch (e) {
        console.warn("[TRACE][useMatchPlayerStats][queryFn:error]", { fixtureId, err: String(e) });
        throw e;
      }
    },
    enabled,
    refetchInterval,
    staleTime: 45_000,
    retry: 1,
  });
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchPlayerStats][state]", {
      fixtureId, lifecycle, enabled, status: q.status, fetchStatus: q.fetchStatus,
      dataUpdatedAt: q.dataUpdatedAt, isFetching: q.isFetching,
    });
  }
  return {
    data: q.data,
    isLoading: enabled && q.isLoading,
    isError: q.isError,
    updatedAt: q.dataUpdatedAt || undefined,
  };
}
